import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const todayYMD = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

const addDaysYMD = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const onlyDigits = (value) => (value || '').replace(/\D+/g, '');

const formatCPF = (value) => {
  const v = onlyDigits(value).slice(0, 11);
  if (v.length <= 3) return v;
  if (v.length <= 6) return `${v.slice(0, 3)}.${v.slice(3)}`;
  if (v.length <= 9) return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
  return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
};

const isValidCPF = (cpfRaw) => {
  const cpf = onlyDigits(cpfRaw);

  if (cpf.length !== 11) return false;
  if (/^(\d)\1+$/.test(cpf)) return false;

  const calcCheck = (base) => {
    let sum = 0;
    let factor = base.length + 1;

    for (let i = 0; i < base.length; i++) {
      sum += parseInt(base[i], 10) * factor;
      factor -= 1;
    }

    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const d1 = calcCheck(cpf.slice(0, 9));
  const d2 = calcCheck(cpf.slice(0, 10));

  return d1 === parseInt(cpf[9], 10) && d2 === parseInt(cpf[10], 10);
};

const formatPhone = (value) => {
  const v = onlyDigits(value).slice(0, 11);
  if (v.length <= 2) return `(${v}`;
  if (v.length <= 6) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  if (v.length <= 10) return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
};

export default function PublicOnlineBookingPage() {
  const { token } = useParams();

  const [clinic, setClinic] = useState(null);
  const [loadingClinic, setLoadingClinic] = useState(true);
  const [clinicError, setClinicError] = useState('');

  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    full_name: '',
    cpf: '',
    phone: '',
    email: '',
    lgpd_consent: false,
  });

  const [verificationId, setVerificationId] = useState('');
  const [code, setCode] = useState('');

  const [professionalId, setProfessionalId] = useState('');
  const [appointmentDate, setAppointmentDate] = useState(todayYMD());
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [procedure, setProcedure] = useState('Consulta');
  const [notes, setNotes] = useState('');

  const [loading, setLoading] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [createdAppointment, setCreatedAppointment] = useState(null);

  useEffect(() => {
    const loadClinic = async () => {
      try {
        setLoadingClinic(true);
        setClinicError('');

        const response = await fetch(`${API_URL}/online-booking/public/${token}`);

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || 'Link de agendamento inválido ou indisponível.');
        }

        const data = await response.json();
        setClinic(data);

        const firstProfessional = data.professionals?.[0];
        if (firstProfessional?.id) {
          setProfessionalId(firstProfessional.id);
        }
      } catch (err) {
        setClinicError(err.message || 'Erro ao carregar agendamento online.');
      } finally {
        setLoadingClinic(false);
      }
    };

    loadClinic();
  }, [token]);

  const maxDate = useMemo(() => {
    const days = Number(clinic?.max_days_ahead || 30);
    return addDaysYMD(days);
  }, [clinic]);

  const selectedProfessional = useMemo(() => {
    return (clinic?.professionals || []).find((p) => p.id === professionalId) || null;
  }, [clinic, professionalId]);

  const selectedProfessionalSpecialty =
    selectedProfessional?.specialties?.[0] || '';

  const clearAlerts = () => {
    setError('');
    setMessage('');
  };

  const startVerification = async (e) => {
    e.preventDefault();
    clearAlerts();

    if (!form.full_name.trim()) {
      setError('Informe seu nome completo.');
      return;
    }

    if (!isValidCPF(form.cpf)) {
    setError('Informe um CPF válido.');
    return;
    }

    if (!form.email.trim()) {
      setError('Informe seu e-mail.');
      return;
    }

    if (!form.lgpd_consent) {
      setError('É necessário autorizar o uso dos dados para prosseguir com o agendamento.');
      return;
    }    

    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/online-booking/verification/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          full_name: form.full_name,
          cpf: form.cpf,
          phone: form.phone,
          email: form.email,
          lgpd_consent: form.lgpd_consent,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível iniciar a confirmação.');
      }

      setVerificationId(data.verification_id);
      setMessage(data.message || 'Código enviado para confirmação.');
      setStep(2);
    } catch (err) {
      setError(err.message || 'Erro ao iniciar confirmação.');
    } finally {
      setLoading(false);
    }
  };

  const confirmVerification = async (e) => {
    e.preventDefault();
    clearAlerts();

    if (!code.trim()) {
      setError('Informe o código recebido.');
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/online-booking/verification/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_id: verificationId, code }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || 'Código inválido.');
      }

      setMessage('Confirmação realizada com sucesso. Agora escolha o horário.');
      setStep(3);
      await loadAvailability();
    } catch (err) {
      setError(err.message || 'Erro ao confirmar código.');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailability = async () => {
    clearAlerts();
    setSelectedSlot(null);

    if (!verificationId || !professionalId || !appointmentDate) return;

    try {
      setAvailabilityLoading(true);

      const response = await fetch(`${API_URL}/online-booking/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verification_id: verificationId,
          professional_id: professionalId,
          appointment_date: appointmentDate,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível carregar os horários.');
      }

      setSlots(data.available_slots || []);
    } catch (err) {
      setSlots([]);
      setError(err.message || 'Erro ao carregar horários.');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  useEffect(() => {
    if (step === 3 && verificationId && professionalId && appointmentDate) {
      loadAvailability();
    }
  }, [step, verificationId, professionalId, appointmentDate]);

  const createAppointment = async () => {
    clearAlerts();

    if (!selectedSlot?.start_time) {
      setError('Escolha um horário disponível.');
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/online-booking/appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verification_id: verificationId,
          professional_id: professionalId,
          appointment_date: appointmentDate,
          start_time: selectedSlot.start_time,
          procedure: procedure || 'Consulta',
          notes,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível concluir o agendamento.');
      }

      setCreatedAppointment({
        ...data.appointment,
        message: data.message,
        requires_manual_confirmation: data.appointment?.requires_manual_confirmation,
        status: data.appointment?.status,
      });
      setStep(4);
    } catch (err) {
      setError(err.message || 'Erro ao concluir agendamento.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingClinic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow p-6 w-full max-w-md text-center">
          <p className="text-gray-700">Carregando agendamento online...</p>
        </div>
      </div>
    );
  }

  if (clinicError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow p-6 w-full max-w-md text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Agendamento indisponível</h1>
          <p className="text-red-600">{clinicError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="w-full max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow border border-gray-100 overflow-hidden">
            <div className="p-6 border-b" style={{ backgroundColor: clinic?.primary_color || '#7c3aed' }}>
            <h1 className="text-2xl font-bold text-white">Agendamento online</h1>
            <p className="text-white/90 mt-1">{clinic?.clinic_name}</p>

            {(clinic?.address || clinic?.phone || clinic?.email) && (
                <div className="mt-3 text-sm text-white/90 space-y-1">
                {clinic?.address && <p>{clinic.address}</p>}
                {clinic?.phone && <p>Telefone/WhatsApp: {clinic.phone}</p>}
                {clinic?.email && <p>E-mail: {clinic.email}</p>}
                </div>
            )}
            </div>

          <div className="p-6">
            <div className="flex flex-wrap gap-2 mb-6">
              {['Identificação', 'Código', 'Horário', 'Concluído'].map((label, index) => (
                <span
                  key={label}
                  className={`px-3 py-1 rounded-full text-sm ${
                    step === index + 1
                      ? 'bg-purple-100 text-purple-700 font-semibold'
                      : step > index + 1
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {index + 1}. {label}
                </span>
              ))}
            </div>

            {message && (
              <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700">
                {message}
              </div>
            )}

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                {error}
              </div>
            )}

            {step === 1 && (
              <form onSubmit={startVerification} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome completo</label>
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={(e) => setForm((prev) => ({ ...prev, full_name: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">CPF</label>
                    <input
                      type="text"
                      value={form.cpf}
                      onChange={(e) => setForm((prev) => ({ ...prev, cpf: formatCPF(e.target.value) }))}
                      placeholder="000.000.000-00"
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Telefone/WhatsApp</label>
                    <input
                      type="text"
                      value={form.phone}
                      onChange={(e) => setForm((prev) => ({ ...prev, phone: formatPhone(e.target.value) }))}
                      placeholder="(00) 00000-0000"
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">E-mail</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enviaremos um código para confirmar o acesso ao agendamento.
                  </p>
                </div>

                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.lgpd_consent}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        lgpd_consent: e.target.checked,
                      }))
                    }
                    className="mt-1"
                  />
                  <span>
                    Li e autorizo o uso dos meus dados para fins de identificação e agendamento,
                    conforme a Política de Privacidade.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 text-white rounded-lg px-4 py-3 font-semibold hover:bg-purple-700 disabled:opacity-60"
                >
                  {loading ? 'Enviando...' : 'Receber código de confirmação'}
                </button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={confirmVerification} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Código de confirmação</label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D+/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-center text-xl tracking-widest"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Informe o código enviado para o seu e-mail.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 text-white rounded-lg px-4 py-3 font-semibold hover:bg-purple-700 disabled:opacity-60"
                >
                  {loading ? 'Confirmando...' : 'Confirmar código'}
                </button>
              </form>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Profissional</label>
                    <select
                      value={professionalId}
                      onChange={(e) => setProfessionalId(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      {(clinic?.professionals || []).map((prof) => (
                        <option key={prof.id} value={prof.id}>
                          {prof.name}
                        </option>
                      ))}
                    </select>

                    {selectedProfessional && (
                      <div className="mt-3 flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                        {selectedProfessional.photo_url ? (
                          <img
                            src={selectedProfessional.photo_url}
                            alt={`Foto de ${selectedProfessional.name}`}
                            className="w-16 h-16 rounded-2xl object-cover border border-gray-200 bg-white"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-2xl border border-dashed border-gray-300 bg-white flex items-center justify-center">
                            <span className="text-xl text-gray-400">👤</span>
                          </div>
                        )}

                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {selectedProfessional.name}
                          </p>

                          {selectedProfessionalSpecialty && (
                            <p className="text-xs text-gray-600">
                              {selectedProfessionalSpecialty}
                            </p>
                          )}

                          {selectedProfessional.is_main && (
                            <p className="text-xs text-purple-700 mt-1">
                              Profissional principal da clínica
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Data</label>
                    <input
                      type="date"
                      value={appointmentDate}
                      min={todayYMD()}
                      max={maxDate}
                      onChange={(e) => setAppointmentDate(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Tipo de consulta</label>
                  <input
                    type="text"
                    value={procedure}
                    onChange={(e) => setProcedure(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Observações, se necessário</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">
                    Horários disponíveis {selectedProfessional ? `com ${selectedProfessional.name}` : ''}
                  </h2>

                  {availabilityLoading ? (
                    <p className="text-gray-600">Carregando horários...</p>
                  ) : slots.length === 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-600">
                      Não há horários disponíveis nesta data.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {slots.map((slot) => {
                        const active = selectedSlot?.start_time === slot.start_time;
                        return (
                          <button
                            key={slot.start_time}
                            type="button"
                            onClick={() => setSelectedSlot(slot)}
                            className={`rounded-lg border px-4 py-3 font-semibold ${
                              active
                                ? 'border-purple-600 bg-purple-600 text-white'
                                : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                            }`}
                          >
                            {slot.start_time}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  disabled={loading || !selectedSlot}
                  onClick={createAppointment}
                  className="w-full bg-purple-600 text-white rounded-lg px-4 py-3 font-semibold hover:bg-purple-700 disabled:opacity-60"
                >
                  {loading ? 'Agendando...' : 'Confirmar agendamento'}
                </button>
              </div>
            )}

            {step === 4 && createdAppointment && (
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-3xl text-green-700">✓</span>
                </div>

                <h2 className="text-2xl font-bold text-gray-900">
                  {createdAppointment.requires_manual_confirmation
                    ? 'Solicitação de agendamento enviada'
                    : 'Consulta agendada com sucesso'}
                </h2>

                <p className="text-gray-600">
                  {createdAppointment.message ||
                    (createdAppointment.requires_manual_confirmation
                      ? 'Aguarde a confirmação da clínica.'
                      : 'Seu agendamento foi confirmado.')}
                </p>                

                <div className="bg-gray-50 rounded-xl p-4 text-left max-w-md mx-auto space-y-2">
                  <p><strong>Paciente:</strong> {createdAppointment.patient_name}</p>
                  <p><strong>Profissional:</strong> {createdAppointment.professional_name}</p>
                  <p><strong>Data:</strong> {createdAppointment.appointment_date}</p>
                  <p><strong>Horário:</strong> {createdAppointment.start_time} às {createdAppointment.end_time}</p>
                  {createdAppointment.status && (
                    <p><strong>Status:</strong> {createdAppointment.status}</p>
                  )}                  
                </div>

                <p className="text-gray-600">
                  {createdAppointment.requires_manual_confirmation
                    ? 'A clínica analisará sua solicitação e entrará em contato para confirmar o horário.'
                    : 'Em caso de dúvidas ou necessidade de alteração, entre em contato diretamente com a clínica.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}